/* eslint-disable */
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Link, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

/* ---------- Shared cache ---------- */
const CACHE_KEY = 'pcdl.cache.browse.series';
const CACHE_TTL_MS = 12 * 60 * 60 * 1000; // 12 hours
const API_URL = 'https://nmt.loveworldapis.com/api/pcdl/series/all';

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null; // { ts, data }
  } catch {
    return null;
  }
}

function writeCache(data) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data }));
  } catch {
    /* ignore */
  }
}

function isFresh(entry) {
  return entry && typeof entry.ts === 'number' && Date.now() - entry.ts < CACHE_TTL_MS;
}

/* ---------- Tiny helpers ---------- */
const strip = (s) => String(s || '');
const toSlug = (s) => strip(s).replace(/[\s+-]/g, '-').toLowerCase();

export default function SearchBar({ placeholder, btn }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  /* dataset + cache */
  const [series, setSeries] = useState([]);
  const [loadingDs, setLoadingDs] = useState(true);
  const [errorDs, setErrorDs] = useState('');

  /* input / UI state */
  const [value, setValue] = useState('');
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);

  const inputRef = useRef(null);
  const panelRef = useRef(null);
  const debounceRef = useRef(null);

  /* Load from cache first; fetch if missing/stale */
  useEffect(() => {
    let cancelled = false;

    async function load() {
      setErrorDs('');
      const cached = readCache();

      if (cached && isFresh(cached)) {
        if (!cancelled) {
          setSeries(Array.isArray(cached.data) ? cached.data : []);
          setLoadingDs(false);
        }
        return;
      }

      // Show stale data while refreshing, if present
      if (cached && !cancelled) {
        setSeries(Array.isArray(cached.data) ? cached.data : []);
        setLoadingDs(false);
      }

      try {
        const res = await fetch(API_URL, {
          method: 'GET',
          headers: {
          },
        });

        const data = await res.json();

        if (cancelled) return;

        const list = Array.isArray(data?.data) ? data.data : [];
        setSeries(list);
        writeCache(list);
      } catch (e) {
        if (!cancelled) setErrorDs('Unable to load titles.');
      } finally {
        if (!cached && !cancelled) setLoadingDs(false);
      }
    }

    load();
    return () => {
      cancelled = true;
      clearTimeout(debounceRef.current);
    };
  }, []);

  /* Debounce to keep typing smooth */
  const onChange = (e) => {
    const v = e.target.value;
    setValue(v);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setQuery(v.trim()), 150);
  };

  /* Filtered list */
  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    if (!q) return [];
    return series
      .filter((s) => strip(s.title).toLowerCase().includes(q))
      .slice(0, 8);
  }, [query, series]);

  /* Keyboard navigation */
  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(filtered.length ? filtered.length - 1 : 0, i + 1));
      setOpen(true);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(-1, i - 1));
    } else if (e.key === 'Enter') {
      if (activeIdx >= 0 && filtered[activeIdx]) {
        const hit = filtered[activeIdx];
        navigate(`/series/${hit.id}/${toSlug(hit.title)}`);
        setOpen(false);
        btn(false);
      } else if (value.trim() !== '') {
        navigate(`results?search_query=${encodeURIComponent(value.trim())}`);
        setOpen(false);
        btn(false);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setActiveIdx(-1);
    }
  };

  /* Close when clicking away */
  useEffect(() => {
    const onDocClick = (e) => {
      if (!panelRef.current || !inputRef.current) return;
      if (
        !panelRef.current.contains(e.target) &&
        !inputRef.current.contains(e.target)
      ) {
        setOpen(false);
        setActiveIdx(-1);
      }
    };

    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const clear = useCallback(() => {
    setValue('');
    setQuery('');
    setActiveIdx(-1);
    setOpen(false);
    inputRef.current?.focus();
  }, []);

  const showLoaderIcon = loadingDs;
  const canSearch = value.trim().length > 0;

  return (
    <div className="w-full text-left">
      <label htmlFor="pcdl-search" className="sr-only">{t('Search')}</label>

      <div className="relative rounded-xl border border-white/15 bg-neutral-800/80 focus-within:bg-neutral-900/90 backdrop-blur px-4 py-2 transition ring-0">
        <div className="flex items-center gap-2">
          <div className="pl-0 text-white/70">
            {showLoaderIcon ? (
              <svg className="h-5 w-5 animate-spin text-white/60" viewBox="0 0 24 24">
                <circle
                  className="opacity-20"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-90"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                />
              </svg>
            ) : (
              <MagnifyingGlassIcon className="h-5 w-5" />
            )}
          </div>

          <input
            ref={inputRef}
            id="pcdl-search"
            autoFocus
            type="text"
            placeholder={t(placeholder)}
            value={value}
            onChange={onChange}
            onKeyDown={onKeyDown}
            onFocus={() => setOpen(true)}
            className="flex-1 bg-transparent outline-none text-white placeholder:text-white/50 text-sm sm:text-base"
          />

          {value ? (
            <button
              type="button"
              onClick={clear}
              className="rounded-xl p-1.5 text-white/70 hover:bg-white/10 hover:text-white transition"
              aria-label="Clear search"
              title="Clear"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          ) : null}

          <Link
            to={canSearch ? `results?search_query=${encodeURIComponent(value.trim())}` : '#'}
            onClick={(e) => {
              if (!canSearch) {
                e.preventDefault();
                return;
              }
              btn(false);
              setOpen(false);
            }}
            className={`rounded-xl px-4 py-1.5 text-sm font-semibold transition ${
              canSearch
                ? 'bg-pcdl-4 text-white hover:brightness-95'
                : 'bg-white/5 text-white/50 cursor-not-allowed'
            }`}
          >
            {t('Search')}
          </Link>
        </div>

        {open && (value || loadingDs) && (
          <div
            ref={panelRef}
            className="absolute z-40 left-0 right-0 mt-2 rounded-xl border border-white/10 bg-neutral-900/95 backdrop-blur shadow-2xl overflow-hidden"
          >
            {loadingDs ? (
              <div className="px-4 py-3 text-sm text-white/80 flex items-center gap-2">
                <svg className="h-4 w-4 animate-spin text-white/70" viewBox="0 0 24 24">
                  <circle
                    className="opacity-20"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-90"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  />
                </svg>
                {t('Loading titles…')}
              </div>
            ) : errorDs ? (
              <div className="px-4 py-3 text-sm text-red-300">{t(errorDs)}</div>
            ) : filtered.length === 0 && value ? (
              <div className="px-4 py-3 text-sm text-white/70">{t('No results')}</div>
            ) : filtered.length > 0 ? (
              <ul className="max-h-[24rem] overflow-y-auto divide-y divide-white/5">
                {filtered.map((it, idx) => (
                  <li key={it.id}>
                    <Link
                      to={`/series/${it.id}/${toSlug(it.title)}`}
                      className={`flex gap-3 p-3 transition ${
                        idx === activeIdx ? 'bg-white/10' : 'hover:bg-white/5'
                      }`}
                      onMouseEnter={() => setActiveIdx(idx)}
                      onClick={() => {
                        setOpen(false);
                        setActiveIdx(-1);
                        btn(false);
                      }}
                    >
                      <img
                        src={it.thumbnail_url || it.cover_url || ''}
                        alt={it.title}
                        loading="lazy"
                        className="h-14 w-14 rounded-md object-cover object-top flex-shrink-0"
                      />
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-white line-clamp-2">
                          {t(it.title)}
                        </p>
                        {it.description ? (
                          <p className="text-[11px] text-white/60 mt-0.5 line-clamp-2">
                            {it.description}
                          </p>
                        ) : null}
                      </div>
                    </Link>
                  </li>
                ))}

                {canSearch && (
                  <li className="p-2">
                    <Link
                      to={`results?search_query=${encodeURIComponent(value.trim())}`}
                      className="block text-center rounded-lg bg-white/10 hover:bg-white/15 text-white text-sm font-semibold px-3 py-2 transition"
                      onClick={() => {
                        setOpen(false);
                        btn(false);
                      }}
                    >
                      {t('See all results for')} “{value.trim()}”
                    </Link>
                  </li>
                )}
              </ul>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

SearchBar.propTypes = {
  placeholder: PropTypes.string.isRequired,
  btn: PropTypes.func.isRequired,
};